import { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { Calendar, Download, TrendingUp } from 'lucide-react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';

type TimePeriod = 'week' | 'month' | 'year' | 'all';

interface RevenueData {
  date: string;
  revenue: number;
  count: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-4">
        <p className="text-sm font-semibold text-gray-700 mb-2">{label}</p>
        <p className="text-lg font-bold text-emerald-600">
          ฿{payload[0].value.toLocaleString()}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          {payload[0].payload.count} รายการ
        </p>
      </div>
    );
  }
  return null;
};

export default function SalepageRevenueChart() {
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('month');
  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadRevenueData();
  }, [timePeriod]);

  const loadRevenueData = async () => {
    setLoading(true);
    try {
      // Load verified salepage payments
      const paymentsSnap = await getDocs(
        query(collection(db, "salePagePayments"), where("status", "==", "verified"))
      );

      const now = new Date();
      let startDate: Date;

      switch (timePeriod) {
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'year':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        case 'all':
          startDate = new Date(0);
          break;
      }

      // Group payments by date
      const revenueMap = new Map<string, { revenue: number; count: number }>();
      let total = 0;
      let count = 0;

      paymentsSnap.docs.forEach(doc => {
        const data = doc.data();
        const createdAt = data.createdAt?.toDate();
        
        if (createdAt && createdAt >= startDate) {
          const dateKey = createdAt.toLocaleDateString('th-TH', {
            year: '2-digit',
            month: 'short',
            ...(timePeriod === 'week' ? { day: 'numeric' } : {})
          });

          const amount = Number(data.amount) || 0;
          const existing = revenueMap.get(dateKey) || { revenue: 0, count: 0 };
          revenueMap.set(dateKey, {
            revenue: existing.revenue + amount,
            count: existing.count + 1
          });

          total += amount;
          count += 1;
        }
      });

      // Convert map to array and sort
      const data = Array.from(revenueMap.entries())
        .map(([date, stats]) => ({
          date,
          revenue: stats.revenue,
          count: stats.count
        }))
        .sort((a, b) => {
          // Simple sort by date string
          return a.date.localeCompare(b.date);
        });

      setRevenueData(data);
      setTotalRevenue(total);
      setTotalCount(count);
    } catch (error) {
      console.error('Error loading revenue data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['วันที่', 'รายได้ (บาท)', 'จำนวนรายการ'];
    const rows = revenueData.map(item => [
      item.date,
      item.revenue.toString(),
      item.count.toString()
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `salepage_revenue_${timePeriod}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const periodLabels: Record<TimePeriod, string> = {
    week: 'รายสัปดาห์',
    month: 'รายเดือน',
    year: 'รายปี',
    all: 'ทั้งหมด'
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">กราฟรายได้</h2>
              <p className="text-sm text-gray-600">วิเคราะห์รายได้ตามช่วงเวลา</p>
            </div>
          </div>
          
          <button
            onClick={exportToCSV}
            disabled={loading || revenueData.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Period Selector */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-5 h-5 text-gray-600" />
          <span className="text-sm font-semibold text-gray-700">ช่วงเวลา:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(periodLabels) as TimePeriod[]).map((period) => (
            <button
              key={period}
              onClick={() => setTimePeriod(period)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                timePeriod === period
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {periodLabels[period]}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Summary */}
      <div className="p-6 border-b border-gray-200 bg-gray-50">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">รายได้รวม</p>
            <p className="text-3xl font-bold text-emerald-600">
              ฿{totalRevenue.toLocaleString()}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600 mb-1">จำนวนรายการ</p>
            <p className="text-3xl font-bold text-blue-600">
              {totalCount.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
              <p className="text-gray-500">กำลังโหลดข้อมูล...</p>
            </div>
          </div>
        ) : revenueData.length === 0 ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center max-w-md">
              <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-xl font-semibold text-gray-700 mb-2">ยังไม่มีข้อมูลรายได้</p>
              <p className="text-sm text-gray-500 mb-4">
                กราฟจะแสดงเมื่อมีการชำระเงินเข้าระบบ
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                <p className="text-sm text-blue-800 mb-2">
                  <strong>💡 วิธีทดสอบระบบ:</strong>
                </p>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• สร้างการชำระเงินทดสอบในระบบ</li>
                  <li>• กราฟจะแสดงข้อมูลแบบ real-time</li>
                  <li>• สามารถเลือกดูตามช่วงเวลาต่างๆ ได้</li>
                  <li>• Export รายงาน CSV สำหรับยื่นภาษี</li>
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={revenueData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `฿${value.toLocaleString()}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '14px', paddingTop: '20px' }}
              />
              <Line
                type="monotone"
                dataKey="revenue"
                name="รายได้"
                stroke="#10b981"
                strokeWidth={3}
                dot={{ fill: '#10b981', r: 5 }}
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
